import { inject, Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import {
  ManualPaymentInput,
  PaymentExecutionInput,
  SmartDepositLegitimationInput,
  SmartDepositRequestOfferInput
} from '@ui/shared/model';
import { map } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  applySmartDepositMutation,
  ApplySmartDepositResponse,
  cancelSmartDepositLegitimationMutation,
  cancelSmartDepositMutation,
  createCardRegistrationMutation,
  createCardRegistrationResponse,
  deleteCreditCardMutation,
  fetchMandateQuery,
  FetchMandateResponse,
  fetchPaymentBrandingQuery,
  FetchPaymentBrandingResponse,
  fetchPaymentRequestQuery,
  FetchPaymentRequestResponse,
  fetchSmartDepositQuery,
  FetchSmartDepositResponse,
  LoadSmartDepositBrandingResponse,
  LoadSmartDepositDictionaryResponse,
  LoadSmartDepositFeeResponse,
  manualPaymentMutation,
  paymentExecutionMutation,
  PaymentExecutionResponse,
  smartDepositBrandingQuery,
  smartDepositDictionaryQuery,
  smartDepositFeeQuery,
  startSmartDepositLegitimationMutation
} from '../queries/payments.queries';

@Injectable({
  providedIn: 'root'
})
export class PaymentsFacade {
  private apollo = inject(Apollo);

  public fetchPaymentRequest(token: string) {
    const fetchPaymentRequestQueryRef =
      this.apollo.watchQuery<FetchPaymentRequestResponse>({
        query: fetchPaymentRequestQuery,
        variables: {
          token
        },
        fetchPolicy: 'no-cache'
      });

    return fetchPaymentRequestQueryRef.valueChanges.pipe(
      filter(response => response.dataState === 'complete'),
      map(response => response.data.fetchPaymentRequest)
    );
  }

  public fetchMandate(token: string, mandateId: string) {
    const fetchMandateQueryRef = this.apollo.watchQuery<FetchMandateResponse>({
      query: fetchMandateQuery,
      variables: {
        token,
        mandateId
      },
      fetchPolicy: 'no-cache'
    });

    return fetchMandateQueryRef.valueChanges.pipe(
      filter(response => response.dataState === 'complete'),
      map(response => response.data.fetchMandate)
    );
  }

  public fetchPaymentBranding(token: string) {
    const fetchPaymentBrandingQueryRef =
      this.apollo.watchQuery<FetchPaymentBrandingResponse>({
        query: fetchPaymentBrandingQuery,
        variables: {
          token
        },
        fetchPolicy: 'no-cache'
      });

    return fetchPaymentBrandingQueryRef.valueChanges.pipe(
      filter(response => response.dataState === 'complete'),
      map(response => response.data.fetchPaymentBranding)
    );
  }

  public fetchSmartDeposit(token: string) {
    const fetchSmartDepositQueryRef =
      this.apollo.watchQuery<FetchSmartDepositResponse>({
        query: fetchSmartDepositQuery,
        variables: {
          token
        },
        fetchPolicy: 'no-cache'
      });

    return fetchSmartDepositQueryRef.valueChanges.pipe(
      filter(response => response.dataState === 'complete'),
      map(response => response.data.fetchSmartDeposit)
    );
  }

  public loadSmartDepositBranding(token: string) {
    return this.apollo
      .query<LoadSmartDepositBrandingResponse>({
        query: smartDepositBrandingQuery,
        variables: {
          token
        },
        fetchPolicy: 'no-cache'
      })
      .pipe(map(response => response.data.smartDepositBranding));
  }

  public manualPayment(input: ManualPaymentInput) {
    return this.apollo.mutate({
      mutation: manualPaymentMutation,
      variables: {
        input
      }
    });
  }

  public paymentExecution(input: PaymentExecutionInput) {
    return this.apollo
      .mutate<PaymentExecutionResponse>({
        mutation: paymentExecutionMutation,
        variables: {
          input
        }
      })
      .pipe(map(response => response.data.paymentExecution));
  }

  public loadSmartDepositFee(deposit: number) {
    return this.apollo
      .query<LoadSmartDepositFeeResponse>({
        query: smartDepositFeeQuery,
        variables: { deposit },
        fetchPolicy: 'no-cache'
      })
      .pipe(map(response => response.data.smartDepositFee));
  }

  public applySmartDeposit(
    token: string,
    smartDepositApplicationPayload: SmartDepositRequestOfferInput
  ) {
    return this.apollo
      .mutate<ApplySmartDepositResponse>({
        mutation: applySmartDepositMutation,
        variables: {
          token,
          smartDepositApplicationPayload
        }
      })
      .pipe(map(response => response.data.applySmartDeposit));
  }

  public cancelSmartDeposit(token: string) {
    return this.apollo.mutate({
      mutation: cancelSmartDepositMutation,
      variables: {
        token
      }
    });
  }

  public startSmartDepositLegitimation(
    token: string,
    input: SmartDepositLegitimationInput
  ) {
    return this.apollo.mutate({
      mutation: startSmartDepositLegitimationMutation,
      variables: {
        token,
        input
      }
    });
  }

  public cancelSmartDepositLegitimation(token: string) {
    return this.apollo.mutate({
      mutation: cancelSmartDepositLegitimationMutation,
      variables: {
        token
      }
    });
  }

  public loadSmartDepositDictionary() {
    return this.apollo
      .query<LoadSmartDepositDictionaryResponse>({
        query: smartDepositDictionaryQuery,
        fetchPolicy: 'no-cache'
      })
      .pipe(map(response => response.data.smartDepositDictionary));
  }

  public createCardRegistration(token: string, cardType: string) {
    return this.apollo
      .mutate<createCardRegistrationResponse>({
        mutation: createCardRegistrationMutation,
        variables: {
          token,
          cardType
        }
      })
      .pipe(map(response => response.data?.createCardRegistration));
  }

  public deleteCreditCard(token: string, creditCardId: string) {
    return this.apollo.mutate({
      mutation: deleteCreditCardMutation,
      variables: {
        token,
        creditCardId
      }
    });
  }
}
